import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React from 'react';
import {Avatar, Body, designSystemToken} from '@lightricks/react-design-system';
import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import type {InvitedCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import invitedStatusText from '@/views/campaigns/creators-tab/utils/invitedStatusText';
import styles from './Column.module.scss';

const INVITED_COLUMN_ID = {
  CreatorName: 'creatorName',
  InvitedAt: 'invitedAt',
  InvitedStatus: 'invitedStatus',
  Email: 'email',
} as const;

const DEFAULT_MIN_COLUMN_SIZE = 100;
const DEFAULT_COLUMN_SIZE = 180;

function getResizableColumnSizing(minSize: number = DEFAULT_MIN_COLUMN_SIZE) {
  return {
    size: Math.max(minSize, DEFAULT_COLUMN_SIZE),
    minSize,
    grow: false,
  } as const;
}

interface UseInvitedCreatorsColumnsOptions {
  onOpenProfile: (row: InvitedCreatorRow) => void;
  showEmailColumn?: boolean;
}

function useInvitedCreatorsColumns({
  onOpenProfile,
  showEmailColumn = false,
}: UseInvitedCreatorsColumnsOptions): MRT_ColumnDef<InvitedCreatorRow>[] {
  const columns: MRT_ColumnDef<InvitedCreatorRow>[] = [
    {
      id: INVITED_COLUMN_ID.CreatorName,
      header: translate('Creator Name'),
      accessorKey: 'creatorName',
      enableSorting: false,
      ...getResizableColumnSizing(),
      Cell: ({row}: {row: MRT_Row<InvitedCreatorRow>}) => {
        const handle = row.original.handle?.trim();
        return (
          <div className={styles.creatorCell}>
            <Avatar
              size="sm"
              src={row.original.avatarUrl ?? undefined}
              type={row.original.avatarUrl ? 'picture' : 'initials'}
              initials={row.original.creatorName.charAt(0)}
            />
            <div className={styles.creatorText}>
              <button
                type="button"
                className={styles.profileButton}
                aria-label={translate('View {name} profile', {
                  name: row.original.creatorName,
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenProfile(row.original);
                }}
              >
                <Body size="md" className={styles.truncatedText}>
                  {row.original.creatorName}
                </Body>
                {handle ? (
                  <Body
                    size="sm"
                    color={designSystemToken('semantic.fg.secondary')}
                    className={styles.truncatedText}
                  >
                    {handle}
                  </Body>
                ) : null}
              </button>
            </div>
          </div>
        );
      },
    },
    {
      id: INVITED_COLUMN_ID.InvitedAt,
      header: translate('Date Invited'),
      accessorKey: 'invitedAt',
      enableSorting: true,
      ...getResizableColumnSizing(),
      Cell: ({row}: {row: MRT_Row<InvitedCreatorRow>}) => (
        <Body size="md">
          {row.original.invitedAt
            ? formattedDate(
                new Date(row.original.invitedAt),
                MONTH_SHORT_DAY_YEAR_FORMAT
              )
            : ''}
        </Body>
      ),
    },
    {
      id: INVITED_COLUMN_ID.InvitedStatus,
      header: translate('Status'),
      accessorKey: 'invitedStatus',
      enableSorting: true,
      sortDescFirst: false,
      ...getResizableColumnSizing(),
      Cell: ({row}: {row: MRT_Row<InvitedCreatorRow>}) => (
        <Body size="md">
          {invitedStatusText({
            invitedStatus: row.original.invitedStatus,
            origination: row.original.origination,
            dateLabel: row.original.invitedAppliedAt
              ? formattedDate(new Date(row.original.invitedAppliedAt), 'MMMM D')
              : '',
          }) ?? ''}
        </Body>
      ),
    },
  ];

  if (showEmailColumn) {
    columns.push({
      id: INVITED_COLUMN_ID.Email,
      header: translate('Email'),
      accessorKey: 'email',
      enableSorting: false,
      ...getResizableColumnSizing(),
      Cell: ({row}: {row: MRT_Row<InvitedCreatorRow>}) => (
        <Body size="md" className={styles.truncatedText}>
          {row.original.email ?? ''}
        </Body>
      ),
    });
  }

  return columns;
}

export default useInvitedCreatorsColumns;
